import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchCaseScenarioPdf, listActiveCaseScenarios } from '../../services/caseScenarios';

/** Full-page PDF view — used when opening a case scenario document in a new browser tab. */
export default function CaseScenarioPdfPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fileName = (searchParams.get('file') ?? '').trim();
  const titleParam = (searchParams.get('title') ?? '').trim();
  const [documentTitle, setDocumentTitle] = useState(titleParam);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (titleParam) {
      setDocumentTitle(titleParam);
      return;
    }
    if (!fileName) {
      setDocumentTitle('');
      return;
    }

    let cancelled = false;
    void listActiveCaseScenarios().then((res) => {
      if (cancelled || !res.success) return;
      const match = res.data.find((s) => s.pdfFileName.trim() === fileName);
      setDocumentTitle(match?.title.trim() ?? '');
    });

    return () => {
      cancelled = true;
    };
  }, [fileName, titleParam]);

  useEffect(() => {
    if (!fileName) {
      setError(t('caseScenarios.pdfLoadFailed'));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchCaseScenarioPdf(fileName).then((res) => {
      if (cancelled) return;
      if (!res.success) {
        setError(res.message ?? t('caseScenarios.pdfLoadFailed'));
        setLoading(false);
        return;
      }
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      setObjectUrl(url);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [fileName, t]);

  const breadcrumbTitle = documentTitle || t('caseScenarios.pdfTitle');

  return (
    <div className="cs-pdf-page">
      <div className="bc" style={{ marginBottom: 12 }}>
        <Link to="/case-scenarios">{t('caseScenarios.breadcrumb')}</Link>
        <span className="bc-sep">/</span>
        <span>{breadcrumbTitle}</span>
      </div>

      <div className="cs-pdf-frame-wrap">
        {loading ? (
          <p className="doc-preview-msg">{t('caseScenarios.loadingPdf')}</p>
        ) : error ? (
          <p className="doc-preview-msg doc-preview-msg--err">{error}</p>
        ) : objectUrl ? (
          <iframe title={breadcrumbTitle} className="cs-pdf-frame" src={objectUrl} />
        ) : (
          <p className="doc-preview-msg">{t('caseScenarios.pdfLoadFailed')}</p>
        )}
      </div>
    </div>
  );
}
